import { formatInTimezone, timestampMs } from './timezone.js'

/**
 * Date/duration helpers used across the admin and supervisor screens.
 *
 * The timezone these render in is resolved by `utils/timezone.ts` (header
 * dropdown → user profile → device). Do not resolve it here — one resolver
 * keeps every screen agreeing on what "now" looks like.
 */

export function formatDate(input: Date | string | null | undefined): string {
  return formatInTimezone(
    input,
    { year: 'numeric', month: 'short', day: 'numeric' },
    undefined,
    '—',
  )
}

export function formatDateTime(input: Date | string | null | undefined): string {
  return formatInTimezone(
    input,
    {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    },
    undefined,
    '—',
  )
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const sec = Math.floor(ms / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

export function formatRelativeTime(input: Date | string): string {
  const ms = timestampMs(input)
  if (Number.isNaN(ms)) return '—'
  const deltaSec = (ms - Date.now()) / 1000
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const abs = Math.abs(deltaSec)
  if (abs < 60) return rtf.format(Math.round(deltaSec), 'second')
  if (abs < 3600) return rtf.format(Math.round(deltaSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(deltaSec / 3600), 'hour')
  return rtf.format(Math.round(deltaSec / 86400), 'day')
}
